/**
 * The screen-pop: the telephone is ringing and here is who it is.
 *
 * The step that makes dispatchers advocate for the product internally, and the reason the
 * whole telephony phase exists. A dispatcher who answers already has the caller's name,
 * their usual pickup and their recent destinations on the screen; without it they ask a
 * customer of four years their address for the fortieth time.
 *
 * Everything shown here arrives with the event. Fetching after it would spend the only
 * seconds that matter — the gap between the first ring and somebody saying hello — on a
 * round trip, and the form would fill in after the conversation had started.
 *
 * A caller nobody recognises still pops, with an empty card and their number. That is not a
 * degraded case: it is a new customer, and the dispatcher still gets somewhere to hang the
 * order.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDispatcherHub, type CallAddress, type IncomingCall } from '../realtime/useDispatcherHub';
import { useDispatch } from '../shell/dispatchContext';
import { usePhone } from '../shell/softphoneContext';
import { useTelephoneContext } from '../shell/telephoneContext';
import { customersApi, type CustomerStanding } from '../orders/api';
import { telephoneApi } from '../telephony/api';
import { Banner, Button, Card, CardHeader, cx, EmptyState, Sub } from '../ui';
import { shouldAnswerOnTake } from './sameCaller';

/** What a dispatcher taking a call hands to the order form. */
export interface CallPrefill {
  callId: string;
  phone: string;
  name: string;
  addresses: CallAddress[];
  pickup: CallAddress | null;
}

export function CallPop({
  onTake,
  className,
}: {
  onTake: (prefill: CallPrefill) => void;
  className?: string;
}) {
  const [ringing, setRinging] = useState<IncomingCall[]>([]);
  // Named `line` because the list below maps over calls of its own, and the telephone's
  // idea of the current call is a different thing from a card on the screen.
  const { call: line, answer, registration } = usePhone();
  const { mine, onDesk } = useTelephoneContext();
  const { setPopped } = useDispatch();

  /** The phones on the desk this dispatcher is at, whose calls are theirs too. */
  const deskExtensions = mine?.desk?.extensions;

  /**
   * The address picked on each card, by call, for a take that happens off the card: a
   * dispatcher who clicked a different doorway and then lifted the handset meant that one.
   */
  const picked = useRef(new Map<string, CallAddress | null>());

  const forget = useCallback((callId: string) => {
    picked.current.delete(callId);
    setRinging((current) => current.filter((call) => call.callId !== callId));
  }, []);

  /** What the order form is given for this caller, the same whichever way they were taken. */
  const prefillFor = useCallback(
    (call: IncomingCall): CallPrefill => ({
      callId: call.callId,
      phone: call.from ?? '',
      name: call.customerName ?? '',
      addresses: call.addresses,
      pickup: picked.current.has(call.callId)
        ? (picked.current.get(call.callId) ?? null)
        : (call.addresses[0] ?? null),
    }),
    [],
  );

  /** The browser telephone, while it is registered. */
  const browserExtension = registration.state === 'registered' ? registration.extension : null;

  /** Whether an answer on this extension was an answer on one of my telephones. */
  const isMine = useCallback(
    (extension: string | null) =>
      extension !== null &&
      (extension === browserExtension || (deskExtensions?.includes(extension) ?? false)),
    [browserExtension, deskExtensions],
  );

  // The telephone card asks whether the caller is already on screen, so that it can offer
  // Answer only for a call that has no pop of its own.
  useEffect(() => {
    setPopped(ringing.map((call) => call.from ?? ''));
  }, [ringing, setPopped]);

  useDispatcherHub({
    onCallRinging: (call) => {
      setRinging((current) =>
        // Guarded against the same call arriving twice. Two API instances briefly holding
        // one firm's connection is the bug the per-tenant lock exists to prevent, and a
        // duplicate card on the screen is how a dispatcher would find out about it.
        current.some((existing) => existing.callId === call.callId) ? current : [...current, call],
      );
    },

    // Somebody answered. It clears from every *other* screen — the blueprint's words are
    // "whoever answers keeps the pop and it clears from the other screens", and the server
    // publishes who took it precisely so each console need not guess.
    //
    // Answered on one of *my* telephones, it is taken: the caller goes into the order form
    // exactly as if `Take` had been pressed, because answering and taking are one act
    // whichever side it started on. A dispatcher who lifted the desk phone's handset is
    // talking to the caller now, and the console's job is to have their name and their
    // addresses in the form by the time they say hello — not to leave a card on screen
    // asking for a second click. Answered in the browser, the click that answered it has
    // already taken the card away, and there is nothing left here to do.
    //
    // A null extension is a real case — the fallback path through `StateChanged` carries
    // none — and the safe reading of it is "not mine", which clears the card as for a
    // colleague. A card that lingers is cleared a moment later by `callFinished`; a card
    // wrongly discarded is gone for good.
    onCallTaken: (taken) => {
      const card = ringing.find((call) => call.callId === taken.callId);

      if (card !== undefined && isMine(taken.extension)) {
        forget(card.callId);
        onTake(prefillFor(card));
        return;
      }

      forget(taken.callId);
    },
    onCallFinished: (finished) => forget(finished.callId),
  });

  if (ringing.length === 0) return null;

  return (
    <div className={className} aria-live="polite">
      {ringing.map((call) => (
        <Ringing
          key={call.callId}
          call={call}
          onPickup={(pickup) => picked.current.set(call.callId, pickup)}
          onTake={() => {
            const prefill = prefillFor(call);

            // Taking a call answers it. One gesture, because there is only one intention: a
            // dispatcher who takes a caller into the order form is going to talk to them,
            // and making them press Answer as well is a second click for nothing — worse,
            // it reads as a choice between talking and taking the order.
            //
            // In the browser, when this browser is the one ringing. On a desk phone, the
            // handset is asked to answer instead — and not waited for: a handset that does
            // not understand the ask keeps ringing, and the dispatcher lifts it, which
            // takes the same road back through `onCallTaken` and finds the card gone. The
            // pop goes to every dispatcher rung while the dial plan decides whose telephone
            // actually rings, so a desk that can see the caller may have no call to answer;
            // there, this fills the form and nothing else.
            if (shouldAnswerOnTake(line, prefill.phone)) {
              answer();
            } else if (onDesk && mine?.desk != null) {
              void telephoneApi.answerOnDesk(call.callId).catch(() => undefined);
            }

            // Cleared from this screen immediately rather than waiting for the server to say
            // it was answered. The dispatcher who clicked is the one taking it, and a card
            // that lingers for a round trip invites a second click.
            forget(call.callId);
            onTake(prefill);
          }}
          onDismiss={() => forget(call.callId)}
        />
      ))}
    </div>
  );
}

function Ringing({
  call,
  onPickup,
  onTake,
  onDismiss,
}: {
  call: IncomingCall;
  /** The dispatcher chose a different address on the card. */
  onPickup: (pickup: CallAddress) => void;
  onTake: () => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();

  // The most-used address, which for a regular customer is where they are standing.
  const usual = call.addresses.length > 0 ? call.addresses[0] : null;

  // Initialised rather than synchronised: each card is keyed by its call, so a second call
  // from the same number is a different card with its own state. The parent is told of a
  // change so a take that happens off the card — the handset lifted — honours it.
  const [pickup, setPickup] = useState<CallAddress | null>(usual);

  function choose(address: CallAddress) {
    setPickup(address);
    onPickup(address);
  }

  const known = call.customerId !== null;

  // What the drivers have said about them. Fetched after the pop rather than carried on the
  // event: it is the one line here that can arrive a moment late without costing the form
  // anything, and the event stays the size it was.
  const [standing, setStanding] = useState<CustomerStanding | null>(null);

  useEffect(() => {
    if (call.customerId === null) return;

    let cancelled = false;
    const customerId = call.customerId;

    void (async () => {
      try {
        const answer = await customersApi.standing(customerId);

        if (!cancelled) setStanding(answer);
      } catch {
        // Nothing said is the ordinary case, and a lookup that failed reads the same.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [call.customerId]);

  return (
    <Card tone="brand">
      <CardHeader
        title={known ? t('calls.known') : t('calls.newCaller')}
        actions={
          <Button size="sm" variant="quiet" onClick={onDismiss}>
            {t('calls.dismiss')}
          </Button>
        }
      />

      {/* First, and unmissable. A dispatcher taking an order from somebody the office has
          blocked is the one outcome this card exists to prevent. */}
      {call.blocked && <Banner tone="danger">{t('calls.blocked')}</Banner>}

      <div className="px-3.5 pt-2.5 pb-3 flex flex-col gap-2">
        <div className="flex items-baseline justify-between gap-2">
          <strong className="font-semibold truncate">
            {call.customerName ?? (known ? t('calls.noName') : t('calls.newCaller'))}
          </strong>
          <span className="text-ink-3 tabular-nums">{call.from ?? t('calls.withheld')}</span>
        </div>

        {/* Which of the firm's numbers they rang, quietly: a trunk can carry several, and
            the firm answers them differently. Absent when the trunk sent none. */}
        {call.to !== null && (
          <span className="text-secondary text-ink-3 tabular-nums -mt-1.5">
            {t('calls.dialledLine', { number: call.to })}
          </span>
        )}

        {call.notes !== null && call.notes.length > 0 && (
          <p className="text-secondary text-ink-2 m-0">{call.notes}</p>
        )}

        {/* The drivers' word about this caller: the stars, how many said so, the no-shows,
            and the latest thing anybody wrote. The one line a dispatcher wants before
            sending a fourth car to somebody who never came out for the first three. */}
        {standing !== null && (standing.ratedByDrivers > 0 || standing.noShows > 0) && (
          <p className="text-secondary m-0 flex flex-wrap gap-x-2">
            {standing.averageDriverRating !== null && (
              <span>
                {t('calls.driversSay', {
                  stars: standing.averageDriverRating.toFixed(1),
                  count: standing.ratedByDrivers,
                })}
              </span>
            )}
            {standing.noShows > 0 && (
              <span className="text-danger font-medium">
                {t('calls.noShows', { count: standing.noShows })}
              </span>
            )}
            {standing.recent[0]?.comment != null && (
              <span className="text-ink-2">“{standing.recent[0].comment}”</span>
            )}
          </p>
        )}

        {call.addresses.length > 0 ? (
          <>
            <span className="text-caps font-semibold tracking-[0.06em] uppercase text-ink-3">
              {t('calls.savedAddresses')}
            </span>
            <ul className="m-0 p-0 list-none flex flex-col gap-0.5">
              {call.addresses.map((address) => (
                <li key={address.addressId}>
                  {/* The chosen one sits on `surface-2` rather than the brand wash: the brand
                      is already spent on this card's border, and a second brand fill inside it
                      would compete with the one button that matters. */}
                  <button
                    type="button"
                    aria-pressed={pickup?.addressId === address.addressId}
                    onClick={() => choose(address)}
                    className={cx(
                      'w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-control text-left',
                      pickup?.addressId === address.addressId ? 'bg-surface-2' : 'hover:bg-surface-2',
                    )}
                  >
                    <span className="truncate">{address.address}</span>
                    {address.label !== null && <Sub>{address.label}</Sub>}
                  </button>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <EmptyState>{known ? t('calls.noAddresses') : t('calls.newCallerHelp')}</EmptyState>
        )}

        {/* Left, not right: it is the one thing this card exists to be pressed, and the eye
            arrives at the left edge of the addresses it has just read. */}
        <Button variant="primary" className="self-start mt-0.5" onClick={onTake}>
          {t('calls.take')}
        </Button>
      </div>
    </Card>
  );
}
